import { useState, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';

export function useBoards() {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('collaborative'); // 'private' or 'collaborative'
  const lastFetchTime = useRef(0);
  const cacheTimeout = 30000; // 30 seconds cache

  const fetchBoards = useCallback(async (forceRefresh = false, mode = viewMode) => {
    try {
      // Prevent refetching too frequently
      const now = Date.now();
      if (!forceRefresh && now - lastFetchTime.current < cacheTimeout) {
        console.log('⚡ Using cached boards data');
        return;
      }

      setLoading(true);
      setError(null);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      console.log(`🔍 Fetching boards - Mode: ${mode}, User: ${user.email}`);

      let query = supabase
        .from('boards')
        .select('*')
        .eq('is_active', true);

      // SIMPLE LOGIC: 
      // - Collaborative mode: Show ALL boards (no filtering)
      // - Private mode: Show only user's boards
      if (mode === 'private') {
        console.log(`🔒 Private mode - filtering by user_id: ${user.id}`);
        query = query.eq('user_id', user.id);
      } else {
        console.log(`🌐 Collaborative mode - showing ALL boards`);
      }
      // In collaborative mode, we don't add any user filter - show everything

      const { data, error: fetchError } = await query.order('position');

      if (fetchError) throw fetchError;
      
      console.log(`📋 Raw boards data:`, data);
      console.log(`📊 Boards count: ${data?.length || 0}`);
      
      // Let's also check what's in the database for debugging
      if (mode === 'collaborative') {
        const { data: allBoards } = await supabase
          .from('boards')
          .select('id, name, user_id, is_active')
          .eq('is_active', true);
        console.log(`🗄️ All boards in database:`, allBoards);
      }
      
      setBoards(data || []);
      lastFetchTime.current = now;
      console.log(`✅ Boards fetched successfully (${mode} mode):`, data?.length || 0);
    } catch (err) {
      console.error('❌ Error fetching boards:', err);
      setError('Failed to fetch boards: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [viewMode]);

  const createBoard = useCallback(async (name, color = '#007bff', status = 'new') => {
    try {
      setError(null);
      
      // Get the highest position
      const { data: maxPositionData } = await supabase
        .from('boards')
        .select('position')
        .order('position', { ascending: false })
        .limit(1);

      const newPosition = (maxPositionData?.[0]?.position || 0) + 1;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error: insertError } = await supabase
        .from('boards')
        .insert({
          name,
          color,
          status,
          position: newPosition,
          user_id: user.id // Always set the creator
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setBoards(prev => [...prev, data].sort((a, b) => a.position - b.position));
      lastFetchTime.current = Date.now();
      return data;
    } catch (err) {
      console.error('Error creating board:', err);
      setError('Failed to create board: ' + err.message);
      throw err;
    }
  }, []);

  const updateBoard = useCallback(async (boardId, updates) => {
    try {
      setError(null);
      
      const { data, error: updateError } = await supabase
        .from('boards')
        .update(updates)
        .eq('id', boardId)
        .select()
        .single();

      if (updateError) throw updateError;

      setBoards(prev => 
        prev.map(board => board.id === boardId ? data : board)
          .sort((a, b) => a.position - b.position)
      );
      
      return data;
    } catch (err) {
      console.error('Error updating board:', err);
      setError('Failed to update board: ' + err.message);
      throw err;
    }
  }, []);

  const updateBoardStatus = useCallback(async (boardId, status) => {
    try {
      setError(null);
      
      const { data, error: updateError } = await supabase
        .from('boards')
        .update({ status })
        .eq('id', boardId)
        .select()
        .single();

      if (updateError) throw updateError;

      setBoards(prev => 
        prev.map(board => board.id === boardId ? { ...board, status } : board)
      );
      
      return data;
    } catch (err) {
      console.error('Error updating board status:', err);
      setError('Failed to update board status: ' + err.message);
      throw err;
    }
  }, []);

  const deleteBoard = useCallback(async (boardId) => {
    try {
      setError(null);
      
      // Check permissions in private mode
      if (viewMode === 'private') {
        const { data: { user } } = await supabase.auth.getUser();
        const board = boards.find(b => b.id === boardId);
        if (board && board.user_id !== user?.id) {
          throw new Error('You can only delete your own boards in private mode');
        }
      }
      
      // Move all tickets from this board to unassigned
      const { error: updateTicketsError } = await supabase
        .from('tickets')
        .update({ board_id: null })
        .eq('board_id', boardId);

      if (updateTicketsError) {
        console.warn('Warning updating tickets:', updateTicketsError);
      }

      // Soft delete the board
      const { error: deleteError } = await supabase
        .from('boards')
        .update({ is_active: false })
        .eq('id', boardId);

      if (deleteError) throw deleteError;

      setBoards(prev => prev.filter(board => board.id !== boardId));
    } catch (err) {
      console.error('Error deleting board:', err);
      setError('Failed to delete board: ' + err.message);
      throw err;
    }
  }, [boards, viewMode]);

  const reorderBoards = useCallback(async (sourceIndex, destinationIndex) => {
    try {
      setError(null);
      
      const reorderedBoards = [...boards];
      const [movedBoard] = reorderedBoards.splice(sourceIndex, 1);
      reorderedBoards.splice(destinationIndex, 0, movedBoard);

      // Update positions
      const updates = reorderedBoards.map((board, index) => ({
        id: board.id,
        position: index + 1
      }));

      // Optimistically update UI
      setBoards(reorderedBoards.map((board, index) => ({
        ...board,
        position: index + 1
      })));

      // Update database
      const updatePromises = updates.map(({ id, position }) =>
        supabase
          .from('boards')
          .update({ position })
          .eq('id', id)
      );

      await Promise.all(updatePromises);

    } catch (err) {
      console.error('Error reordering boards:', err);
      setError('Failed to reorder boards: ' + err.message);
      // Revert on error
      await fetchBoards(true);
    }
  }, [boards, fetchBoards]);

  // View mode management
  const switchViewMode = useCallback(async (newMode) => {
    try {
      setViewMode(newMode);
      
      // Update user session
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('user_sessions')
          .update({ view_mode: newMode })
          .eq('user_id', user.id);

        // Save user preference
        await supabase
          .from('user_preferences')
          .upsert({
            user_id: user.id,
            view_mode: newMode,
            updated_at: new Date().toISOString()
          }, { onConflict: 'user_id' });
      }

      // Fetch boards for new mode
      await fetchBoards(true, newMode);
      
      console.log(`🔄 Switched to ${newMode} mode`);
    } catch (err) {
      console.error('Error switching view mode:', err);
      setError('Failed to switch view mode');
    }
  }, [fetchBoards]);

  // Load user preferences
  const loadUserPreferences = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_preferences')
        .select('view_mode')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading user preferences:', error);
        return;
      }

      if (data?.view_mode) {
        setViewMode(data.view_mode);
        console.log(`👤 Loaded user preference: ${data.view_mode} mode`);
      }
    } catch (err) {
      console.error('Error loading user preferences:', err);
    }
  }, []);

  // Helper functions
  const getBoardById = useCallback((boardId) => {
    return boards.find(board => board.id == boardId);
  }, [boards]);

  const getBoardByName = useCallback((boardName) => {
    return boards.find(board => board.name === boardName);
  }, [boards]);

  // Simplified permission check
  const canEditBoard = useCallback((board, userId) => {
    if (!board || !userId) return false;
    // In collaborative mode: everyone can edit everything
    // In private mode: only owner can edit
    return viewMode === 'collaborative' || board.user_id === userId;
  }, [viewMode]);

  return {
    boards,
    loading,
    error,
    viewMode,
    fetchBoards,
    createBoard,
    updateBoard,
    updateBoardStatus,
    deleteBoard,
    reorderBoards,
    switchViewMode,
    loadUserPreferences,
    getBoardById,
    getBoardByName,
    canEditBoard
  };
}